import { type Hex, isHex } from 'viem';
import { z } from 'zod';

/**
 * Canonical RSV (r||s||v) 65-byte signature hex string.
 */
export const rsvSignatureHexSchema = z
  .custom<Hex>((value) => typeof value === 'string', {
    error: 'Signature must be a string',
  })
  .refine((value) => isHex(value, { strict: true }) && value.length > 2, {
    error: 'Signature must be a 0x-prefixed hex string',
  })
  .refine((value) => value.length === 132, {
    error:
      'Signature must be a 65-byte hex string starting with 0x (132 chars)',
  })
  .refine(
    (value) => {
      const tailV = value.slice(130, 132).toLowerCase();
      return tailV === '1b' || tailV === '1c';
    },
    {
      error:
        'Signature must be canonical RSV with v at tail (expected 0x...1b or 0x...1c)',
    }
  )
  .brand<'RSV_SIGNATURE_HEX'>();

/**
 * Canonical VRS (v||r||s) 65-byte signature hex string.
 */
const vrsSignatureHexSchema = z
  .custom<Hex>((value) => typeof value === 'string', {
    error: 'Signature must be a string',
  })
  .refine((value) => isHex(value, { strict: true }) && value.length > 2, {
    error: 'Signature must be a 0x-prefixed hex string',
  })
  .refine((value) => value.length === 132, {
    error:
      'Signature must be a 65-byte hex string starting with 0x (132 chars)',
  })
  .refine(
    (value) => {
      const headV = value.slice(2, 4).toLowerCase();
      return headV === '1b' || headV === '1c';
    },
    {
      error:
        'Signature must be canonical VRS with v at head (expected 0x1b... or 0x1c...)',
    }
  )
  .brand<'VRS_SIGNATURE_HEX'>();

export type RSVSignatureHex = z.infer<typeof rsvSignatureHexSchema>;
export type VRSSignatureHex = z.infer<typeof vrsSignatureHexSchema>;

export function validateAndParseRSVSignatureHex(
  input: unknown
): RSVSignatureHex {
  return rsvSignatureHexSchema.parse(input);
}

export function validateAndParseVRSSignatureHex(
  input: unknown
): VRSSignatureHex {
  return vrsSignatureHexSchema.parse(input);
}

/**
 * Normalize a 65-byte signature into canonical VRS format for on-chain signature packing.
 * Assumes stored signatures are raw canonical RSV (r||s||v) with v in {1b,1c}.
 */
export function convertRSVSignatureHexToVRS(
  signature: RSVSignatureHex
): VRSSignatureHex {
  const raw = signature.slice(2);
  const tailV = raw.slice(128, 130).toLowerCase();
  return validateAndParseVRSSignatureHex(`0x${tailV}${raw.slice(0, 128)}`);
}
